/**
 * WiedervorlageMaxSetting — Admin-Einstellungen → Sonstiges.
 *
 * Batch-13 / Paket D: max. Anzahl Wiedervorlagen je Anfrage (Default 5,
 * Bereich 1..20). Danach beendet der Scheduler-Command
 * `wiedervorlage:auto-close` die Anfrage automatisch, sobald die letzte
 * erlaubte Wiedervorlage überfällig und nicht erledigt ist.
 *
 * Liest/schreibt gezielt nur diesen einen Key über `wiedervorlageMaxApi`
 * (src/lib/wiedervorlage.ts) — bewusst NICHT über den generischen
 * `systemSettingsApi` aus src/lib/api.ts (wird parallel von anderer Stelle
 * gepflegt, s. Task-Notiz Paket D).
 */
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, SectionTitle, Button, Field, Input } from '../../components/ui';
import { getThemeColors } from '../../lib/theme';
import { wiedervorlageMaxApi } from '../../lib/wiedervorlage';

interface WiedervorlageMaxSettingProps {
  dark: boolean;
}

export function WiedervorlageMaxSetting({ dark }: WiedervorlageMaxSettingProps) {
  const t = getThemeColors(dark);
  const { t: tr } = useTranslation();

  const [value, setValue] = useState<string>('5');
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const max = await wiedervorlageMaxApi.get();
        if (!cancelled) setValue(String(max));
      } catch {
        if (!cancelled) setLoadError(tr('settings.wiedervorlage_max.load_error') || 'Konnte Einstellung nicht laden.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [tr]);

  const save = async () => {
    const parsed = Number(value);
    if (!Number.isInteger(parsed) || parsed < 1 || parsed > 20) {
      setSaveError(tr('settings.wiedervorlage_max.error_range') || 'Bitte eine Zahl zwischen 1 und 20 eingeben.');
      return;
    }
    setSaving(true);
    setSaveError(null);
    setSaved(false);
    try {
      const updated = await wiedervorlageMaxApi.update(parsed);
      setValue(String(updated));
      setSaved(true);
    } catch {
      setSaveError(tr('settings.wiedervorlage_max.error_save') || 'Speichern fehlgeschlagen.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card dark={dark} style={{ padding: 20, maxWidth: 420 }}>
      <SectionTitle
        dark={dark}
        title={tr('settings.wiedervorlage_max.title') || 'Wiedervorlage'}
        subtitle={tr('settings.wiedervorlage_max.subtitle') || 'Maximale Anzahl Wiedervorlagen je Anfrage, bevor sie automatisch beendet wird.'}
      />
      {loading ? (
        <div style={{ fontSize: 13, color: t.muted, padding: '8px 0' }}>{tr('common.loading') || 'Lädt…'}</div>
      ) : (
        <>
          {loadError && <div style={{ fontSize: 12, color: '#b42318', marginBottom: 8 }}>{loadError}</div>}
          <Field label={tr('settings.wiedervorlage_max.field_label') || 'Maximum (1–20)'}>
            <Input
              dark={dark}
              type="number"
              min={1}
              max={20}
              value={value}
              onChange={e => { setValue(e.target.value); setSaved(false); setSaveError(null); }}
              aria-label={tr('settings.wiedervorlage_max.field_label') || 'Wiedervorlage-Maximum'}
            />
          </Field>
          <div style={{ marginTop: 10, display: 'flex', alignItems: 'center', gap: 10 }}>
            <Button variant="primary" size="sm" dark={dark} onClick={() => void save()} disabled={saving}>
              {saving ? (tr('settings.wiedervorlage_max.saving') || 'Speichert…') : (tr('common.action.save') || 'Speichern')}
            </Button>
            {saved && <span style={{ fontSize: 12, color: '#039855', fontWeight: 600 }}>{tr('settings.wiedervorlage_max.saved') || 'Gespeichert.'}</span>}
            {saveError && <span style={{ fontSize: 12, color: '#b42318', fontWeight: 600 }}>{saveError}</span>}
          </div>
        </>
      )}
    </Card>
  );
}
